/**
 * MapPage hosts the historical playback experience. The live fleet map lives
 * in Layout as a persistent canvas; this page is the panel that renders on
 * top of it when the dispatcher wants to scrub through one vehicle's day.
 *
 * Picking a vehicle + date here fetches a decimated track from the backend
 * and exposes a scrub-bar timeline. The polyline is drawn directly via Leaflet
 * primitives in the playback panel rather than going through FleetMap, since
 * the live map continues to render live positions underneath.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CircleMarker, MapContainer, Polyline, Marker, Tooltip, useMap } from "react-leaflet";
import L, { type LatLngExpression } from "leaflet";
import { api, ApiError, type VehicleHistoryPoint } from "../api/client";
import type { RouteShape, RouteStop, Vehicle } from "../api/types";
import { InvalidateOnResize, MapTileLayers, RouteNetworkOverlay, RouteStopsOverlay } from "../components/MapBaseLayers";

const PLAYBACK_RATES = [1, 2, 4, 8, 16];
const GAP_THRESHOLD_SEC = 90;
// Gaps shorter than this are filled with an estimated straight-line segment.
// 360s (6 min) matches the Android driver app's store-and-forward buffer
// window: positions delayed by network hiccups shorter than this are typically
// still delivered once reconnected, whereas gaps longer than that usually mean
// genuine data loss. Open to tuning once more operational data is available.
const SHORT_GAP_FILL_SEC = 360;
// An upper bound on what a bus can physically do, not a service speed limit.
// This was 80, which silently excluded every genuine sample above it — so the
// journey summary's "max speed" could never report more than 80 km/h no matter
// how fast the bus actually went.
const MAX_PLAUSIBLE_SPEED_KMH = 150;

// Region gate for drawn geometry. Historical rows include null-island (0,0)
// fixes and lat/lon-swapped points that land in China; a single one of those
// drew a solid line across Asia and expanded the fitted bounds to half the
// globe, shrinking the real track to a few pixels.
const BOUNDS_PK = { minLat: 23, maxLat: 38, minLon: 60, maxLon: 78 };

function isPlausibleCoord(p: { lat: number; lon: number }): boolean {
  return (
    Number.isFinite(p.lat) &&
    Number.isFinite(p.lon) &&
    !(p.lat === 0 && p.lon === 0) &&
    p.lat >= BOUNDS_PK.minLat && p.lat <= BOUNDS_PK.maxLat &&
    p.lon >= BOUNDS_PK.minLon && p.lon <= BOUNDS_PK.maxLon
  );
}

// Exclusive upper bound for a history window: the top of the minute after
// `hhmm` on `date`, in service-local time, rolling into the next day when the
// window ends at 23:59.
function exclusiveEnd(date: string, hhmm: string): string {
  const [h, m] = hhmm.split(":").map((v) => parseInt(v, 10));
  const end = new Date(`${date}T00:00:00+05:00`);
  end.setUTCMinutes(end.getUTCMinutes() + h * 60 + m + 1);
  const pkt = new Date(end.getTime() + 5 * 3600 * 1000);
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${pkt.getUTCFullYear()}-${p2(pkt.getUTCMonth() + 1)}-${p2(pkt.getUTCDate())}` +
    `T${p2(pkt.getUTCHours())}:${p2(pkt.getUTCMinutes())}:00+05:00`;
}
// 60m is the Android app's own GPS-fix acceptance threshold; 100m leaves
// headroom for device/OS-level variance while still catching the coarse
// network-fallback fixes (~150m) that produce visible glitches.
const MAX_PLAUSIBLE_ACCURACY_M = 100;

interface LowAccuracyZone {
  name: string;
  lat: number;
  lon: number;
  radiusMeters: number;
}

// TODO(confirm-with-lead): radii are estimates from ~1.1km investigation
// grid buckets, not surveyed boundaries — may need tightening/loosening
// once more per-zone accuracy data comes in.
const KNOWN_LOW_ACCURACY_ZONES: LowAccuracyZone[] = [
  { name: "Islamabad core", lat: 33.68, lon: 73.05, radiusMeters: 1200 },
  { name: "Rawat corridor", lat: 33.50, lon: 73.185, radiusMeters: 2000 },
];

function findContainingZone(lat: number, lon: number): LowAccuracyZone | null {
  for (const zone of KNOWN_LOW_ACCURACY_ZONES) {
    if (haversineKm(lat, lon, zone.lat, zone.lon) * 1000 <= zone.radiusMeters) {
      return zone;
    }
  }
  return null;
}

interface OutlierInfo {
  zone: LowAccuracyZone | null;
}

function isPlausibleSpeed(speed: number | undefined): boolean {
  return speed != null && speed >= 0 && speed <= MAX_PLAUSIBLE_SPEED_KMH;
}

function isPlausibleSegment(
  p1: VehicleHistoryPoint,
  p2: VehicleHistoryPoint,
): boolean {
  const gapMs = new Date(p2.time).getTime() - new Date(p1.time).getTime();
  if (gapMs <= 0 || gapMs > GAP_THRESHOLD_SEC * 1000) return false;
  const distKm = haversineKm(p1.lat, p1.lon, p2.lat, p2.lon);
  const impliedKmh = distKm / (gapMs / 3_600_000);
  return impliedKmh <= MAX_PLAUSIBLE_SPEED_KMH;
}

const BUS_PATH =
  "M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4S4 2.5 4 6v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM18 11H6V6h12v5z";

function busIcon(outlier?: OutlierInfo): L.DivIcon {
  let border = "3px solid #fff";
  let shadow = "0 1px 6px rgba(0,0,0,.4)";
  if (outlier) {
    if (outlier.zone) {
      border = "3px dashed #B36F00";
      shadow = "0 1px 6px rgba(0,0,0,.4), 0 0 0 3px rgba(179,111,0,.25)";
    } else {
      border = "3px dashed #ef4444";
      shadow = "0 1px 6px rgba(0,0,0,.4), 0 0 0 3px rgba(239,68,68,.3)";
    }
  }
  return L.divIcon({
    html:
      `<div style="width:30px;height:30px;border-radius:50%;background:var(--brand-primary);` +
      `border:${border};box-shadow:${shadow};` +
      `display:flex;align-items:center;justify-content:center;">` +
      `<svg viewBox="0 0 24 24" width="17" height="17" fill="#fff"><path d="${BUS_PATH}"/></svg>` +
      `</div>`,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

// The service day in network-local time, not in the browser's zone. A
// dispatcher (or an auditor) working from another timezone otherwise opened
// the page on the wrong day by default.
function todayIso(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Karachi",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "01";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function currentMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtTimeShort(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function vehicleLabel(v: Vehicle): string {
  return v.vehicle_label || v.vehicle_id || v.id.slice(0, 8);
}

function speedColor(speed: number | undefined): string {
  const s = speed ?? 0;
  if (s < 5) return "#E24B4A";
  if (s < 20) return "#EF9F27";
  if (s < 40) return "#1D9E75";
  return "#378ADD";
}

const SPEED_LEGEND = [
  { color: "#E24B4A", label: "Stopped (0–5 km/h)" },
  { color: "#EF9F27", label: "Slow (5–20)" },
  { color: "#1D9E75", label: "Normal (20–40)" },
  { color: "#378ADD", label: "Fast (40+)" },
];

function speedLabel(speed: number | undefined): string {
  const s = speed ?? 0;
  if (s < 5) return "Stopped";
  if (s < 20) return "Slow";
  if (s < 40) return "Normal";
  return "Fast";
}

function buildCumulativeDistances(points: VehicleHistoryPoint[]): number[] {
  const cum = new Array<number>(points.length);
  cum[0] = 0;
  for (let i = 1; i < points.length; i++) {
    const segDist = isPlausibleSegment(points[i - 1], points[i])
      ? haversineKm(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon)
      : 0;
    cum[i] = cum[i - 1] + segDist;
  }
  return cum;
}

/** Client-side CSV of the already-fetched (real, DB-sourced) track — no
 *  separate export endpoint needed since the data is already in hand. */
function downloadCsv(
  vehicle: string,
  date: string,
  points: VehicleHistoryPoint[],
  outliers: Map<number, OutlierInfo>,
  corrections: Map<number, CorrectedPosition>,
) {
  const header = "time,lat,lon,speed_kmh,bearing,accuracy_m,flagged,flag_reason,corrected_lat,corrected_lon\n";
  const body = points
    .map((p, i) => {
      const info = outliers.get(i);
      const corr = corrections.get(i);
      const flagged = info ? "yes" : "";
      const reason = info
        ? (info.zone ? `known low-accuracy area (${info.zone.name})` : "GPS glitch")
        : "";
      const corrLat = corr ? corr.lat.toFixed(7) : "";
      const corrLon = corr ? corr.lon.toFixed(7) : "";
      return `${p.time},${p.lat},${p.lon},${p.speed ?? ""},${p.bearing ?? ""},${p.accuracy ?? ""},${flagged},${reason},${corrLat},${corrLon}`;
    })
    .join("\n");
  const blob = new Blob([header + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${vehicle || "vehicle"}-${date}-track.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

interface GpsSegment {
  startPct: number;
  widthPct: number;
  /**
   * "gap" is a dropout *inside* the reporting window — the vehicle was
   * reporting either side of it, so the data really was lost.
   * "off" is the time before the first fix and after the last: the bus was
   * not reporting at all, which for a vehicle that runs one shift is simply
   * out of service. Conflating the two made a healthy bus that ran 05:34
   * to 23:59 read "46 gaps (15h37m)", of which 5h34m was just the night.
   */
  type: "coverage" | "gap" | "off";
  startTime: string;
  endTime: string;
}

function buildTimelineSegments(points: VehicleHistoryPoint[], date: string): GpsSegment[] {
  if (points.length === 0) return [];

  const dayStartMs = new Date(`${date}T00:00:00+05:00`).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const segments: GpsSegment[] = [];

  const firstMs = new Date(points[0].time).getTime();
  if (firstMs - dayStartMs > GAP_THRESHOLD_SEC * 1000) {
    segments.push({
      startPct: 0,
      widthPct: ((firstMs - dayStartMs) / dayMs) * 100,
      type: "off",
      startTime: "00:00",
      endTime: fmtHhMm(firstMs),
    });
  }

  let coverStart = firstMs;
  for (let i = 0; i < points.length - 1; i++) {
    const curMs = new Date(points[i].time).getTime();
    const nextMs = new Date(points[i + 1].time).getTime();
    if (nextMs - curMs > GAP_THRESHOLD_SEC * 1000) {
      segments.push({
        startPct: ((coverStart - dayStartMs) / dayMs) * 100,
        widthPct: ((curMs - coverStart) / dayMs) * 100,
        type: "coverage",
        startTime: fmtHhMm(coverStart),
        endTime: fmtHhMm(curMs),
      });
      segments.push({
        startPct: ((curMs - dayStartMs) / dayMs) * 100,
        widthPct: ((nextMs - curMs) / dayMs) * 100,
        type: "gap",
        startTime: fmtHhMm(curMs),
        endTime: fmtHhMm(nextMs),
      });
      coverStart = nextMs;
    }
  }

  const lastMs = new Date(points[points.length - 1].time).getTime();
  segments.push({
    startPct: ((coverStart - dayStartMs) / dayMs) * 100,
    widthPct: ((lastMs - coverStart) / dayMs) * 100,
    type: "coverage",
    startTime: fmtHhMm(coverStart),
    endTime: fmtHhMm(lastMs),
  });

  // On the current service day the bar must stop at now. Hours that have not
  // happened yet are not "not reporting" — a bus whose last fix arrived two
  // minutes ago was being credited with the whole rest of the evening as
  // absence. For a past day this clamp is a no-op.
  const dayEndMs = Math.min(dayStartMs + dayMs, Date.now());
  if (dayEndMs - lastMs > GAP_THRESHOLD_SEC * 1000) {
    segments.push({
      startPct: ((lastMs - dayStartMs) / dayMs) * 100,
      widthPct: ((dayEndMs - lastMs) / dayMs) * 100,
      type: "off",
      startTime: fmtHhMm(lastMs),
      // Labelled with the real end of the window, which on today is now.
      endTime: fmtHhMm(dayEndMs),
    });
  }

  return segments;
}

function fmtHhMm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fmtDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

interface JourneySummaryData {
  distanceKm: number;
  activeDurationMs: number;
  avgSpeedKmh: number;
  maxSpeedKmh: number;
}

// TODO(confirm-with-lead): Speed-based flagging flags points where both the
// hop TO and FROM exceed MAX_PLAUSIBLE_SPEED_KMH. Accuracy-based flagging
// catches coarse network-fallback fixes (reported accuracy > 100m) that may
// look fine by speed but are unreliable positions. Both thresholds and
// strategy (flag vs. smooth vs. drop) need confirmation with real data.
// Thresholds are identical inside and outside known low-accuracy zones —
// only the visual treatment differs, not detection.
function flagOutlierPoints(points: VehicleHistoryPoint[]): Map<number, OutlierInfo> {
  const flagged = new Map<number, OutlierInfo>();
  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    let isFlagged = false;

    if (curr.accuracy != null && curr.accuracy > MAX_PLAUSIBLE_ACCURACY_M) {
      isFlagged = true;
    } else if (i > 0 && i < points.length - 1) {
      const prev = points[i - 1];
      const next = points[i + 1];

      const gapToPrev = new Date(curr.time).getTime() - new Date(prev.time).getTime();
      const gapFromNext = new Date(next.time).getTime() - new Date(curr.time).getTime();

      if (gapToPrev > 0 && gapFromNext > 0) {
        const speedToPrev = haversineKm(prev.lat, prev.lon, curr.lat, curr.lon) / (gapToPrev / 3_600_000);
        const speedToNext = haversineKm(curr.lat, curr.lon, next.lat, next.lon) / (gapFromNext / 3_600_000);
        if (speedToPrev > MAX_PLAUSIBLE_SPEED_KMH && speedToNext > MAX_PLAUSIBLE_SPEED_KMH) {
          isFlagged = true;
        }
      }
    }

    if (isFlagged) {
      flagged.set(i, { zone: findContainingZone(curr.lat, curr.lon) });
    }
  }
  return flagged;
}

interface CorrectedPosition {
  lat: number;
  lon: number;
}

interface TrailSeg {
  positions: LatLngExpression[];
  color: string;
  type: "normal" | "corrected" | "estimated";
  gapDuration?: number;
}

// For each flagged point, interpolate a corrected position between the
// nearest unflagged point before and after it (weighted by timestamp).
// Points with no valid anchor on one side are left uncorrected.
function buildCorrectedPositions(
  points: VehicleHistoryPoint[],
  outliers: Map<number, OutlierInfo>,
): Map<number, CorrectedPosition> {
  const corrections = new Map<number, CorrectedPosition>();

  for (const [idx] of outliers) {
    let before = -1;
    for (let j = idx - 1; j >= 0; j--) {
      if (!outliers.has(j)) { before = j; break; }
    }
    let after = -1;
    for (let j = idx + 1; j < points.length; j++) {
      if (!outliers.has(j)) { after = j; break; }
    }
    if (before < 0 || after < 0) continue;

    const pB = points[before];
    const pA = points[after];
    const tB = new Date(pB.time).getTime();
    const tA = new Date(pA.time).getTime();
    const tC = new Date(points[idx].time).getTime();
    if (tA <= tB) continue;

    const t = (tC - tB) / (tA - tB);
    corrections.set(idx, {
      lat: pB.lat + t * (pA.lat - pB.lat),
      lon: pB.lon + t * (pA.lon - pB.lon),
    });
  }

  return corrections;
}

function findNearestPointIndex(points: VehicleHistoryPoint[], targetMs: number): number {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < points.length; i++) {
    const dist = Math.abs(new Date(points[i].time).getTime() - targetMs);
    if (dist < bestDist) { bestDist = dist; bestIdx = i; }
  }
  return bestIdx;
}

// TODO(review): Distance/speed totals currently exclude hops through flagged
// points entirely (isPlausibleSegment returns false). Using corrected
// (interpolated) positions instead would give a more realistic total distance
// and average speed — but changes what those numbers mean: "distance the bus
// actually GPS-confirmed" vs. "distance including our best-guess corrections."
// Deliberately left as-is pending team-lead review.
function computeJourneySummary(points: VehicleHistoryPoint[]): JourneySummaryData {
  if (points.length === 0) return { distanceKm: 0, activeDurationMs: 0, avgSpeedKmh: 0, maxSpeedKmh: 0 };

  let distanceKm = 0;
  let activeDurationMs = 0;
  let maxSpeed = 0;
  let speedSum = 0;
  let speedCount = 0;

  for (const p of points) {
    if (isPlausibleSpeed(p.speed)) {
      speedSum += p.speed!;
      speedCount++;
      if (p.speed! > maxSpeed) maxSpeed = p.speed!;
    }
  }

  for (let i = 0; i < points.length - 1; i++) {
    if (!isPlausibleSegment(points[i], points[i + 1])) {
      const gapMs = new Date(points[i + 1].time).getTime() - new Date(points[i].time).getTime();
      if (gapMs > 0 && gapMs <= GAP_THRESHOLD_SEC * 1000) activeDurationMs += gapMs;
      continue;
    }
    const gapMs = new Date(points[i + 1].time).getTime() - new Date(points[i].time).getTime();
    distanceKm += haversineKm(points[i].lat, points[i].lon, points[i + 1].lat, points[i + 1].lon);
    activeDurationMs += gapMs;
  }

  return {
    distanceKm,
    activeDurationMs,
    avgSpeedKmh: speedCount > 0 ? speedSum / speedCount : 0,
    maxSpeedKmh: maxSpeed,
  };
}

export default function MapPage() {
  const { theme = "dark" } = useOutletContext<{ theme?: "dark" | "light" }>();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthIso());
  const [fromTime, setFromTime] = useState<string>("00:00");
  const [toTime, setToTime] = useState<string>("23:59");
  const [intervalSecs, setIntervalSecs] = useState<number>(10);
  const [points, setPoints] = useState<VehicleHistoryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Roster failures are tracked separately. They used to share `error` with
  // the history fetch, and since the empty state and the track render are both
  // gated on `!error`, one failed vehicle-list call latched and blanked the
  // page for the rest of the session.
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);
  const [vehiclesReloadKey, setVehiclesReloadKey] = useState(0);

  const [stops, setStops] = useState<RouteStop[]>([]);
  const [routeShapes, setRouteShapes] = useState<RouteShape[]>([]);

  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [daysLoading, setDaysLoading] = useState(false);
  const [daysError, setDaysError] = useState<string | null>(null);

  // Scrub state
  const [cursor, setCursor] = useState<number>(0);
  const [playing, setPlaying] = useState<boolean>(false);
  const [rate, setRate] = useState<number>(1);
  const playTimer = useRef<number | null>(null);
  const rateRef = useRef(rate);
  rateRef.current = rate;

  useEffect(() => {
    let cancelled = false;
    setVehiclesError(null);
    api.listVehicles()
      .then((res) => {
        if (cancelled) return;
        const list: Vehicle[] = Array.isArray(res)
          ? res
          : (res && typeof res === "object" && Array.isArray((res as { data?: unknown }).data)
              ? (res as { data: Vehicle[] }).data
              : []);
        setVehicles(list);
        if (list.length === 0) {
          setVehiclesError("No vehicles found for your agency.");
        } else if (!selectedVehicleId && list.length > 0) {
          setSelectedVehicleId(list[0].id);
        }
      })
      .catch((err) => {
        console.error("[MapPage] Failed to load vehicles:", err);
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setVehiclesError("Session expired — please sign in again");
        } else if (err instanceof ApiError && err.status === 429) {
          setVehiclesError("Rate limited by the API gateway — vehicle list unavailable. Retry in a moment.");
        } else {
          setVehiclesError("Failed to load vehicles");
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesReloadKey]);

  useEffect(() => {
    let cancelled = false;
    api.listRouteStops()
      .then((res) => { if (!cancelled) setStops(res); })
      .catch((err) => { console.warn("[MapPage] Failed to load route stops:", err); });
    api.listRouteShapes()
      .then((res) => { if (!cancelled) setRouteShapes(Array.isArray(res) ? res : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const fetchDaysRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!selectedVehicleId) return;
    let cancelled = false;

    const doFetch = () => {
      setDaysLoading(true);
      setDaysError(null);
      setAvailableDays([]);
      api.getVehicleHistoryDays(selectedVehicleId)
        .then((res) => {
          if (!cancelled) setAvailableDays(res.days ?? []);
        })
        .catch((err) => {
          if (cancelled) return;
          if ((err instanceof ApiError && err.status === 401) || err?.message === "Unauthorized") {
            setDaysError("Session expired — please sign in again.");
          } else {
            setDaysError("Failed to load available dates.");
          }
        })
        .finally(() => {
          if (!cancelled) setDaysLoading(false);
        });
    };

    fetchDaysRef.current = doFetch;
    doFetch();
    return () => { cancelled = true; };
  }, [selectedVehicleId]);

  useEffect(() => {
    if (!selectedVehicleId || !serviceDate) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPoints([]);
    setCursor(0);
    setPlaying(false);
    api
      .getVehicleHistory(selectedVehicleId, {
        from: `${serviceDate}T${fromTime}:00+05:00`,
        // Exclusive end at the top of the following minute. The old
        // `:59` bound dropped everything in the final second of the window,
        // which at 23:59 meant the last second of the service day.
        to: exclusiveEnd(serviceDate, toTime),
        intervalSeconds: intervalSecs,
      })
      .then((res) => {
        if (cancelled) return;
        // Filtered once, here, rather than at each point of use. Bad fixes
        // were being excluded from the polyline and the fitted bounds but
        // still counted in "N POINTS" and still written into the CSV export —
        // so the page reported, and handed the operator, coordinates it was
        // refusing to draw. One gate keeps the count, the summary, the
        // timeline, the export and the map describing the same track.
        setPoints((res.points ?? []).filter(isPlausibleCoord));
      })
      .catch((err) => {
        console.error("[MapPage] Failed to load history:", err);
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load history");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedVehicleId, serviceDate, fromTime, toTime, intervalSecs]);

  useEffect(() => {
    if (!playing) {
      if (playTimer.current != null) {
        window.clearInterval(playTimer.current);
        playTimer.current = null;
      }
      return;
    }
    if (points.length < 2) return;

    let idx = cursor;
    let accumulator = 0;
    let lastTick = performance.now();
    const loopId = Math.random().toString(36).slice(2, 6);
    console.log(`[playback] start id=${loopId} rate=${rateRef.current} cursor=${idx} pts=${points.length}`);

    const tick = () => {
      const now = performance.now();
      const dt = now - lastTick;
      lastTick = now;
      accumulator += dt;

      const r = rateRef.current;
      let advanced = false;

      while (accumulator > 0) {
        const gapMs = idx < points.length - 1
          ? new Date(points[idx + 1].time).getTime() - new Date(points[idx].time).getTime()
          : 200;
        const delay = Math.min(2000 / r, Math.max(50, gapMs / r));

        if (accumulator < delay) break;

        accumulator -= delay;
        idx += 1;
        advanced = true;

        if (idx >= points.length - 1) {
          console.log(`[playback] end id=${loopId} rate=${r}`);
          setCursor(points.length - 1);
          setPlaying(false);
          return;
        }
      }

      if (advanced) {
        console.log(`[playback] id=${loopId} rate=${r} idx=${idx} acc=${accumulator.toFixed(0)} now=${now.toFixed(0)}`);
        setCursor(idx);
      }
    };

    playTimer.current = window.setInterval(tick, 30);

    return () => {
      console.log(`[playback] cleanup id=${loopId}`);
      if (playTimer.current != null) {
        window.clearInterval(playTimer.current);
        playTimer.current = null;
      }
    };
    // cursor intentionally excluded — the loop tracks position with a local
    // variable; including cursor re-triggers the effect every tick.
    // External position changes (seek, scrub, reset) all flip `playing`,
    // which restarts the loop from the new cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, points]);

  const outlierIndices = useMemo(() => flagOutlierPoints(points), [points]);

  const correctedPositions = useMemo(
    () => buildCorrectedPositions(points, outlierIndices),
    [points, outlierIndices],
  );

  const speedSegments = useMemo(() => {
    const segs: (TrailSeg & { startIdx: number })[] = [];
    const trailPoints = points.slice(0, Math.max(1, cursor + 1));
    for (let i = 0; i < trailPoints.length - 1; i++) {
      const corr_i = correctedPositions.get(i);
      const corr_next = correctedPositions.get(i + 1);
      const from: LatLngExpression = corr_i
        ? [corr_i.lat, corr_i.lon]
        : [trailPoints[i].lat, trailPoints[i].lon];
      const to: LatLngExpression = corr_next
        ? [corr_next.lat, corr_next.lon]
        : [trailPoints[i + 1].lat, trailPoints[i + 1].lon];
      const gapMs = new Date(trailPoints[i + 1].time).getTime() - new Date(trailPoints[i].time).getTime();

      if (gapMs > SHORT_GAP_FILL_SEC * 1000) {
        continue;
      }
      if (gapMs > GAP_THRESHOLD_SEC * 1000) {
        segs.push({ positions: [from, to], color: "#9ca3af", type: "estimated", gapDuration: gapMs, startIdx: i });
        continue;
      }

      const segType: "normal" | "corrected" = (corr_i || corr_next) ? "corrected" : "normal";
      const color = speedColor(trailPoints[i + 1].speed);
      const prev = segs.length > 0 ? segs[segs.length - 1] : null;
      if (prev && prev.color === color && prev.type === segType) {
        prev.positions.push(to);
      } else {
        segs.push({ positions: [from, to], color, type: segType, startIdx: i });
      }
    }
    return segs;
  }, [points, cursor, correctedPositions]);

  const timelineSegments = useMemo(
    () => buildTimelineSegments(points, serviceDate),
    [points, serviceDate],
  );

  const summary = useMemo(() => computeJourneySummary(points), [points]);
  const cumulativeKm = useMemo(() => buildCumulativeDistances(points), [points]);

  const gapAtCursor = useMemo(() => {
    if (points.length < 2) return null;
    if (cursor < points.length - 1) {
      const ms = new Date(points[cursor + 1].time).getTime() - new Date(points[cursor].time).getTime();
      if (ms > GAP_THRESHOLD_SEC * 1000) return ms;
    }
    if (cursor > 0) {
      const ms = new Date(points[cursor].time).getTime() - new Date(points[cursor - 1].time).getTime();
      if (ms > GAP_THRESHOLD_SEC * 1000) return ms;
    }
    return null;
  }, [points, cursor]);

  const seekToPercent = (pct: number) => {
    if (playTimer.current != null) {
      window.clearInterval(playTimer.current);
      playTimer.current = null;
    }
    const dayStartMs = new Date(`${serviceDate}T00:00:00+05:00`).getTime();
    const dayMs = 24 * 60 * 60 * 1000;
    const targetMs = dayStartMs + (pct / 100) * dayMs;
    const idx = findNearestPointIndex(points, targetMs);
    setCursor(idx);
    setPlaying(false);
    requestAnimationFrame(() => setPlaying(idx < points.length - 1));
  };

  const currentPoint = points[cursor];
  const initialCenter: LatLngExpression = currentPoint
    ? [currentPoint.lat, currentPoint.lon]
    : points.length > 0
      ? [points[0].lat, points[0].lon]
      : [33.6844, 73.0479];

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);
  const scrubPct = points.length > 1 ? (cursor / (points.length - 1)) * 100 : 0;

  return (
    <div className="page history-page">
      <div className="history-header">
        <div>
          <h1>Vehicle History</h1>
          <p className="secondary-text">Replay any vehicle's GPS track for a service day.</p>
        </div>
        <button
          className="btn primary history-export-btn"
          type="button"
          disabled={points.length === 0}
          onClick={() => downloadCsv(selectedVehicle ? vehicleLabel(selectedVehicle) : selectedVehicleId, serviceDate, points, outlierIndices, correctedPositions)}
        >
          Export track
        </button>
      </div>

      {vehiclesError && (
        <div className="banner banner-error">
          {vehiclesError}
          <button
            className="btn ghost"
            type="button"
            style={{ marginLeft: 8 }}
            onClick={() => setVehiclesReloadKey((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      <div className="card history-filter-card">
        <label className="history-field">
          <span>Vehicle</span>
          <select
            value={selectedVehicleId}
            onChange={(e) => setSelectedVehicleId(e.target.value)}
          >
            <option value="">— select —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
            ))}
          </select>
        </label>
        <label className="history-field">
          <span>Month</span>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </label>
        <label className="history-field">
          <span>Date</span>
          <input
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
          />
        </label>
        <label className="history-field">
          <span>Sample every</span>
          <select
            value={intervalSecs}
            onChange={(e) => setIntervalSecs(parseInt(e.target.value, 10))}
          >
            <option value={5}>5 s</option>
            <option value={10}>10 s</option>
            <option value={30}>30 s</option>
            <option value={60}>1 min</option>
          </select>
        </label>
        <div className="history-points-pill">
          <span className="pill sev-info">{points.length.toLocaleString()} POINTS</span>
        </div>
      </div>

      {points.length > 0 && (
        <JourneySummary data={summary} />
      )}

      {selectedVehicleId && (
        <AvailableDaysBar
          days={availableDays}
          loading={daysLoading}
          error={daysError}
          onRetry={() => fetchDaysRef.current()}
          selectedDate={serviceDate}
          onSelectDate={setServiceDate}
          month={selectedMonth}
        />
      )}

      {error && <div className="banner banner-error">{error}</div>}
      {loading && <div className="empty">Loading…</div>}
      {!loading && !error && points.length === 0 && selectedVehicleId && (
        <div className="empty">
          <p><strong>{selectedVehicle ? vehicleLabel(selectedVehicle) : "Vehicle"}</strong> — {serviceDate}</p>
          <p className="secondary-text">
            No GPS track found. Possible reasons:
          </p>
          <ul className="secondary-text" style={{ textAlign: "left", display: "inline-block", margin: "8px auto" }}>
            <li>Vehicle was not in service on this date</li>
            <li>Driver was not signed in to the tablet</li>
            <li>GPS data is only available via device heartbeats</li>
          </ul>
          <div>
            <button
              className="btn ghost"
              type="button"
              onClick={() => {
                const prev = new Date(serviceDate + "T00:00:00");
                prev.setDate(prev.getDate() - 1);
                const y = prev.getFullYear();
                const m = String(prev.getMonth() + 1).padStart(2, "0");
                const d = String(prev.getDate()).padStart(2, "0");
                setServiceDate(`${y}-${m}-${d}`);
              }}
            >
              Try previous day
            </button>
            {availableDays.length > 0 && (
              <button
                className="btn ghost"
                type="button"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  const latest = availableDays[availableDays.length - 1];
                  if (latest) {
                    setServiceDate(latest);
                    setSelectedMonth(latest.slice(0, 7));
                  }
                }}
              >
                Jump to latest ({availableDays[availableDays.length - 1]})
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && !error && timelineSegments.length > 0 && (
        <GpsTimeline
          segments={timelineSegments}
          date={serviceDate}
          onSeek={seekToPercent}
          scrubPct={scrubPct}
          totalPoints={points.length}
          cursor={cursor}
          onScrub={(idx) => { setCursor(idx); setPlaying(false); }}
          playing={playing}
          onPlayPause={() => setPlaying((p) => !p)}
          rate={rate}
          onRate={setRate}
          onReset={() => { setCursor(0); setPlaying(false); }}
          currentTime={currentPoint?.time}
          currentSpeed={currentPoint?.speed}
        />
      )}

      {selectedVehicleId && (
        <div className="card history-time-filter-card">
          <label className="history-field">
            <span>From</span>
            <input
              type="time"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
            />
          </label>
          <label className="history-field">
            <span>To</span>
            <input
              type="time"
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
            />
          </label>
        </div>
      )}

      {points.length > 0 && (
        <>
          <div className="card history-map-card">
            <MapContainer
              center={initialCenter}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
            >
              <MapTileLayers theme={theme} />
              <InvalidateOnResize />
              <FitOnLoad points={points} />
              <RouteNetworkOverlay
                shapes={routeShapes}
                highlightRouteId={currentPoint?.route_id}
                theme={theme}
              />
              <RouteStopsOverlay stops={stops} theme={theme} />
              {speedSegments.map((seg) =>
                seg.type === "estimated" ? (
                  <Polyline
                    key={`est-${seg.startIdx}`}
                    positions={seg.positions}
                    pathOptions={{ color: "#9ca3af", weight: 2, opacity: 0.5, dashArray: "4 6" }}
                  >
                    <Tooltip sticky>
                      Estimated — no GPS data for this {fmtDuration(seg.gapDuration!)} stretch
                    </Tooltip>
                  </Polyline>
                ) : (
                  <Polyline
                    key={`${seg.type}-${seg.startIdx}`}
                    positions={seg.positions}
                    pathOptions={
                      seg.type === "corrected"
                        ? { color: seg.color, weight: 3, opacity: 0.85, dashArray: "8 4" }
                        : { color: seg.color, weight: 4 }
                    }
                  />
                ),
              )}
              {outlierIndices.size > 0 && Array.from(outlierIndices.entries()).map(([i, info]) => {
                const p = points[i];
                if (!p) return null;
                const inZone = info.zone != null;
                return (
                  <CircleMarker
                    key={`outlier-${i}`}
                    center={[p.lat, p.lon]}
                    radius={10}
                    pathOptions={{
                      color: inZone ? "#B36F00" : "#ef4444",
                      weight: 3,
                      fillColor: inZone ? "#B36F00" : "#ef4444",
                      fillOpacity: 0.15,
                      dashArray: "6 4",
                      opacity: inZone ? 0.7 : 1,
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -10]}>
                      {inZone
                        ? `Flagged — known low-accuracy area (${info.zone!.name})`
                        : "Flagged — GPS glitch (implausible speed or accuracy)"}
                    </Tooltip>
                  </CircleMarker>
                );
              })}
              {currentPoint && (
                <Marker position={[currentPoint.lat, currentPoint.lon]} icon={busIcon(outlierIndices.get(cursor))} />
              )}
            </MapContainer>
          </div>

          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", fontSize: "0.85em", color: "var(--text-3)" }}>
            {SPEED_LEGEND.map((item) => (
              <span key={item.color} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ background: item.color, width: 16, height: 4, borderRadius: 2, display: "inline-block" }} />
                {item.label}
              </span>
            ))}
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 16, height: 4, borderRadius: 2, display: "inline-block", backgroundImage: "repeating-linear-gradient(90deg, var(--text-3) 0 6px, transparent 6px 10px)" }} />
              Corrected
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 16, height: 4, borderRadius: 2, display: "inline-block", backgroundImage: "repeating-linear-gradient(90deg, #9ca3af 0 4px, transparent 4px 8px)" }} />
              Estimated
            </span>
          </div>

          <div className="history-playback-stats">
            <span>{fmtTimeShort(currentPoint?.time)}</span>
            <span className="history-pb-sep">·</span>
            <span style={{ color: speedColor(currentPoint?.speed) }}>{currentPoint?.speed != null ? `${Math.round(currentPoint.speed)} km/h` : "—"}</span>
            <span className="history-pb-sep">·</span>
            <span>{speedLabel(currentPoint?.speed)}</span>
            <span className="history-pb-sep">·</span>
            <span>{(cumulativeKm[cursor] ?? 0).toFixed(1)} km covered</span>
            {gapAtCursor != null && (
              <>
                <span className="history-pb-sep">·</span>
                <span style={{ color: "var(--sev-warn, #B36F00)" }}>No GPS data — {fmtDuration(gapAtCursor)}</span>
              </>
            )}
          </div>
          <div className="secondary-text history-range-caption">
            {fmtTime(points[0]?.time)} → {fmtTime(points[points.length - 1]?.time)}
          </div>
        </>
      )}
    </div>
  );
}

function FitOnLoad({ points }: { points: VehicleHistoryPoint[] }) {
  const map = useMap();
  useEffect(() => {
    const usable = points;
    if (usable.length === 0) {
      map.setView([33.6844, 73.0479], 12);
      return;
    }
    // A single point used to leave the map framed on the previously selected
    // vehicle, because MapContainer is never remounted and `center` is only
    // read once. Centre on it instead.
    if (usable.length === 1) {
      map.setView([usable[0].lat, usable[0].lon], 15);
      return;
    }
    const bounds = L.latLngBounds(
      [usable[0].lat, usable[0].lon],
      [usable[0].lat, usable[0].lon]
    );
    for (const p of usable) bounds.extend([p.lat, p.lon]);
    map.fitBounds(bounds, { padding: [40, 40] });
    requestAnimationFrame(() => map.invalidateSize());
  }, [points, map]);
  return null;
}

function AvailableDaysBar({
  days,
  loading,
  error,
  onRetry,
  selectedDate,
  onSelectDate,
  month,
}: {
  days: string[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedDate: string;
  onSelectDate: (d: string) => void;
  month: string;
}) {
  const [justSelected, setJustSelected] = useState(false);
  const prevDateRef = useRef(selectedDate);

  useEffect(() => {
    if (prevDateRef.current === selectedDate) return;
    prevDateRef.current = selectedDate;
    setJustSelected(true);
    const timer = setTimeout(() => setJustSelected(false), 600);
    return () => clearTimeout(timer);
  }, [selectedDate]);

  const dataSet = useMemo(() => new Set(days), [days]);

  const calendarDays = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const result: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      result.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return result;
  }, [month]);

  if (loading) {
    return (
      <div className="card history-days-card">
        <span className="history-days-label">Available days</span>
        <span className="secondary-text">Loading available dates…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card history-days-card history-days-card--error">
        <span className="history-days-label">Available days</span>
        <span className="banner banner-error inline">{error}</span>
        <button className="btn ghost" type="button" onClick={onRetry}>Retry</button>
      </div>
    );
  }

  const daysWithData = calendarDays.filter((d) => dataSet.has(d)).length;

  return (
    <div className="card history-days-card">
      <span className="history-days-label">Available days</span>
      <div className="history-days-scroll">
        {calendarDays.map((d) => {
          const dt = new Date(d + "T00:00:00");
          const dayName = dt.toLocaleDateString([], { weekday: "short" });
          const dayNum = dt.getDate();
          const mon = dt.toLocaleDateString([], { month: "short" });
          const isSelected = d === selectedDate;
          const hasData = dataSet.has(d);
          return (
            <button
              key={d}
              type="button"
              className={[
                "history-day-chip",
                hasData ? "has-data" : "no-data",
                isSelected ? "selected" : "",
                isSelected && justSelected ? "just-selected" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => onSelectDate(d)}
              title={d}
            >
              <span className="history-day-weekday">{dayName}</span>
              <span className="history-day-num">{dayNum}</span>
              <span className="history-day-month">{mon}</span>
            </button>
          );
        })}
      </div>
      <span className="secondary-text">{daysWithData} of {calendarDays.length} days have data</span>
    </div>
  );
}

function GpsTimeline({
  segments,
  date,
  onSeek,
  scrubPct,
  totalPoints,
  cursor,
  onScrub,
  playing,
  onPlayPause,
  rate,
  onRate,
  onReset,
  currentTime,
  currentSpeed,
}: {
  segments: GpsSegment[];
  date: string;
  onSeek?: (pct: number) => void;
  scrubPct: number;
  totalPoints: number;
  cursor: number;
  onScrub: (idx: number) => void;
  playing: boolean;
  onPlayPause: () => void;
  rate: number;
  onRate: (r: number) => void;
  onReset: () => void;
  currentTime?: string;
  currentSpeed?: number;
}) {
  const [hover, setHover] = useState<GpsSegment | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const dragged = useRef(false);

  const pctFromEvent = (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
  };

  const handleBarPointerDown = (e: React.MouseEvent<HTMLDivElement>) => {
    dragged.current = false;
    const startX = e.clientX;

    const onMove = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - startX) > 3) dragged.current = true;
      if (!dragged.current) return;
      const p = pctFromEvent(ev);
      const i = Math.round((p / 100) * Math.max(0, totalPoints - 1));
      onScrub(i);
    };
    const onUp = (ev: MouseEvent) => {
      if (onSeek) {
        const pct = pctFromEvent(dragged.current ? ev : e);
        onSeek(pct);
      }
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const totalCoverageMs = segments
    .filter((s) => s.type === "coverage")
    .reduce((sum, s) => sum + (s.widthPct / 100) * 24 * 60 * 60 * 1000, 0);
  // Only in-window dropouts count as gaps. Time before the first fix and
  // after the last is reported separately as "not reporting" — it is usually
  // the vehicle being out of service, not data loss.
  const totalGapMs = segments
    .filter((s) => s.type === "gap")
    .reduce((sum, s) => sum + (s.widthPct / 100) * 24 * 60 * 60 * 1000, 0);
  const gapCount = segments.filter((s) => s.type === "gap").length;
  const totalOffMs = segments
    .filter((s) => s.type === "off")
    .reduce((sum, s) => sum + (s.widthPct / 100) * 24 * 60 * 60 * 1000, 0);

  return (
    <div className="card history-timeline-card">
      <div className="history-timeline-head">
        <span className="history-timeline-label">GPS Coverage — {date}</span>
        <span className="secondary-text">
          {fmtDuration(totalCoverageMs)} coverage
          {gapCount > 0 && ` · ${gapCount} gap${gapCount !== 1 ? "s" : ""} (${fmtDuration(totalGapMs)})`}
          {totalOffMs > 0 && ` · ${fmtDuration(totalOffMs)} not reporting`}
        </span>
      </div>
      <div
        className="history-timeline-bar"
        ref={barRef}
        onMouseDown={handleBarPointerDown}
      >
        {segments.map((seg, i) => (
          <div
            key={i}
            className={`history-tl-seg ${seg.type}`}
            style={{ left: `${seg.startPct}%`, width: `${Math.max(seg.widthPct, 0.3)}%` }}
            onMouseEnter={() => setHover(seg)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {totalPoints > 1 && (
          <div className="history-tl-cursor" style={{ left: `${scrubPct}%` }}>
            <div
              className="history-tl-cursor-tooltip"
              style={
                scrubPct < 10
                  ? { transform: `translateX(${-scrubPct * 5}%)` }
                  : scrubPct > 90
                    ? { transform: `translateX(${-50 - (scrubPct - 90) * 5}%)` }
                    : undefined
              }
            >
              {fmtTimeShort(currentTime)}
              {currentSpeed != null ? ` · ${Math.round(currentSpeed)} km/h` : ""}
            </div>
          </div>
        )}
        {hover && (
          <div
            className="history-tl-tooltip"
            style={{ left: `${hover.startPct + hover.widthPct / 2}%` }}
          >
            {hover.type === "gap" ? "Signal lost" : hover.type === "off" ? "Not reporting" : "Coverage"}: {hover.startTime} – {hover.endTime}
          </div>
        )}
      </div>
      <div className="history-timeline-axis">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>23:59</span>
      </div>

      <div className="history-tl-controls">
        <button
          className="history-play-btn"
          type="button"
          onClick={onPlayPause}
          disabled={cursor >= totalPoints - 1 && !playing}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>

        <div className="history-timeline-legend">
          <span className="history-tl-legend-item">
            <span className="history-tl-legend-dot coverage" /> Coverage
          </span>
          <span className="history-tl-legend-item">
            <span className="history-tl-legend-dot gap" /> Signal lost (&gt;90s)
          </span>
          <span className="history-tl-legend-item">
            <span className="history-tl-legend-dot off" /> Not reporting
          </span>
        </div>

        <div className="history-rate-group">
          {PLAYBACK_RATES.map((r) => (
            <button
              key={r}
              type="button"
              className={"history-rate-btn" + (rate === r ? " active" : "")}
              onClick={() => onRate(r)}
            >
              {r}×
            </button>
          ))}
        </div>

        <button className="btn ghost" type="button" onClick={onReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

function JourneySummary({ data }: { data: JourneySummaryData }) {
  return (
    <div className="card history-summary-card">
      <span className="history-summary-label">Journey Summary</span>
      <div className="history-summary-grid">
        <div className="history-summary-stat">
          <span className="history-summary-value">{data.distanceKm.toFixed(1)} km</span>
          <span className="history-summary-key">Distance</span>
        </div>
        <div className="history-summary-stat">
          <span className="history-summary-value">{fmtDuration(data.activeDurationMs)}</span>
          <span className="history-summary-key">Active duration</span>
        </div>
        <div className="history-summary-stat">
          <span className="history-summary-value">{data.avgSpeedKmh.toFixed(1)} km/h</span>
          <span className="history-summary-key">Avg speed</span>
        </div>
        <div className="history-summary-stat">
          <span className="history-summary-value">{Math.round(data.maxSpeedKmh)} km/h</span>
          <span className="history-summary-key">Max speed</span>
        </div>
      </div>
    </div>
  );
}
